import React, { useCallback, useEffect, useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import CompactFooterLeft from 'components/customer/compact_footer_left';
import CompositionManager from 'components/customer/composition_manager';
import ConversationAssignment from 'components/customer/conversation_history/conversation_assignment';
import ConversationTopics from 'components/customer/conversation_topics';
import CustomerContext from 'components/customer/customer_context';
import { useCustomerFooterRef } from 'components/customer/customer_footer_ref_context';
import useDevice from 'components/hooks/use_device';
import { Device } from 'scripts/lib/device_detector';
import WorkflowButtons from 'components/customer/workflow_buttons';

function CustomerFooter({ isCompactMode = false }) {
  const { setFooterNode } = useCustomerFooterRef();

  if (useDevice() === Device.Phone) {
    return <MobileFooter />;
  }

  if (isCompactMode) {
    return (
      <div className="customerV2-footer" data-aid="customerV2-footer" ref={setFooterNode}>
        <CompactFooterLeft />
        <CompositionAreaWrapper>
          <CompositionManager />
        </CompositionAreaWrapper>
        <CompactWorkflowWrapper>
          <WorkflowButtons />
        </CompactWorkflowWrapper>
      </div>
    );
  }

  return (
    <div className="customerV2-footer" data-aid="customerV2-footer" ref={setFooterNode}>
      <ConversationAssignment />
      <CompositionAreaWrapper>
        <CompositionManager />
      </CompositionAreaWrapper>
      <ConversationTopics />
      <WorkflowButtons />
    </div>
  );
}

function MobileFooter() {
  const { compositionId } = React.useContext(CustomerContext);
  const { setFooterNode } = useCustomerFooterRef();
  const [isExpanded, setIsExpanded] = useState(false);
  const toggle = useCallback(() => setIsExpanded(prev => !prev), []);

  useEffect(() => {
    if (compositionId) {
      setIsExpanded(false);
    }
  }, [compositionId]);

  return (
    <MobileFooterContainer data-aid="customerV2-footer" ref={setFooterNode}>
      {!compositionId && (
        <FloatingChevron data-aid="mobileFooterExpand" onClick={toggle}>
          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronUp} />
        </FloatingChevron>
      )}
      {isExpanded && (
        <MobileDropUp>
          <CompactFooterLeft />
        </MobileDropUp>
      )}
      <MobileFooterBar>
        <MobileCompositionArea>
          <CompositionManager />
        </MobileCompositionArea>
        <WorkflowButtons />
      </MobileFooterBar>
    </MobileFooterContainer>
  );
}

CustomerFooter.propTypes = {
  isCompactMode: PropTypes.bool,
};

export default React.memo(CustomerFooter);

const CompositionAreaWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  height: 100%;
  min-width: 0;
`;

const CompactWorkflowWrapper = styled.div`
  flex-shrink: 0;

  > div {
    margin-left: 4px;
    padding: 0 16px 0 4px;
  }

  .composerButtons-portal-active {
    width: auto;
  }

  .compositionForm-formButtons [data-aid='cancel-composition'] {
    margin-right: 8px;
  }
`;

const MobileFooterContainer = styled.div`
  position: relative;
  z-index: 3;
`;

const FloatingChevron = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray400};
  cursor: pointer;
  display: flex;
  font-size: 20px;
  height: 24px;
  justify-content: center;
  left: 50%;
  padding: 0;
  position: absolute;
  top: -24px;
  transform: translateX(-50%);
  z-index: 4;

  &:active {
    opacity: 0.5;
  }
`;

const MobileDropUp = styled.div`
  background-color: ${p => p.theme.colors.white};
  border-top: 1px solid ${p => p.theme.colors.gray200};
  box-shadow: ${p => p.theme.boxShadow.mediumInverted};
  height: 48px;
  overflow: visible;
  position: relative;

  /* Override compact footer fixed width for mobile full-width */
  > div {
    width: 100%;
    min-width: 0;
  }

  /* Both menus: fixed to viewport edges with 8px margins, positioned
     just above the drop-up + footer bar (48 + 52 = 100px from bottom). */
  .searchableConversationAssignment-menu-block,
  .conversationTopic-menu-container {
    bottom: 100px;
    left: 8px;
    max-width: none;
    min-width: 0;
    position: fixed;
    right: 8px;
    width: auto;
    z-index: 100;
  }

  .searchableConversationAssignment-menu-block .searchableMenu {
    width: 100%;
  }
`;

const MobileFooterBar = styled.div`
  align-items: center;
  background-color: ${p => p.theme.colors.white};
  box-shadow: ${p => p.theme.boxShadow.mediumInverted};
  display: flex;
  height: 52px;
  padding: 0 8px;
`;

const MobileCompositionArea = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  height: 100%;
  min-width: 0;
`;
